import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getFirstNameFromStorage } from '../../../utils/helper';
import heroMountains from '../../../assets/progress-hero-fjord-mountains.png';
import heroMountainsWide from '../../../assets/progress-hero-fjord-mountains-wide.jpg';
import ForecastProgressFooter from '../forecast/ForecastProgressFooter';
import { SEE_ALL_MONEY_MOVES_PATH } from '../your-quick-richuels/constants';
import {
  CalendarIcon,
  CheckIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  CompletedIcon,
  InProgressIcon,
  MoreIcon,
  PlayIcon,
  TrendUpIcon,
} from './icons';
import { fetchUserCategories, fetchUserContents, handleApiError } from '../../../utils/api';
import {
  buildProgressBoards,
  nextContent,
  readPlaybackStartedIds,
  sessionPath,
  summarizeContents,
} from './progressData';
import styles from './YourProgressPage.module.css';

function useCountUp(value, active) {
  const [display, setDisplay] = useState(0);

  useEffect(() => {
    if (!active) return undefined;
    const target = Math.max(0, Number(value) || 0);
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduceMotion || target === 0) {
      setDisplay(target);
      return undefined;
    }
    const duration = 780;
    const start = performance.now();
    let frame = 0;
    const tick = (now) => {
      const t = Math.min(1, (now - start) / duration);
      const eased = 1 - (1 - t) ** 3;
      setDisplay(Math.round(target * eased));
      if (t < 1) frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [active, value]);

  return display;
}

function PaceRing({ completed, total, shownCompleted = completed }) {
  const size = 72;
  const stroke = 7;
  const radius = (size - stroke) / 2;
  const circumference = 2 * Math.PI * radius;
  const progress = total > 0 ? Math.min(1, Math.max(0, completed / total)) : 0;

  return (
    <svg className={styles.paceRing} viewBox={`0 0 ${size} ${size}`} aria-hidden="true">
      <circle
        cx={size / 2}
        cy={size / 2}
        r={radius}
        fill="none"
        stroke="rgba(11, 138, 122, 0.16)"
        strokeWidth={stroke}
      />
      <circle
        className={styles.paceRingFill}
        cx={size / 2}
        cy={size / 2}
        r={radius}
        fill="none"
        stroke="#0b8a7a"
        strokeWidth={stroke}
        strokeLinecap="round"
        strokeDasharray={circumference}
        strokeDashoffset={circumference * (1 - progress)}
        transform={`rotate(-90 ${size / 2} ${size / 2})`}
      />
      <text x="50%" y="51%" textAnchor="middle" dominantBaseline="middle" className={styles.paceRingText}>
        {shownCompleted}/{total}
      </text>
    </svg>
  );
}

function actionSteps(session) {
  return (session.actions || []).map((item) => ({
    id: item.id,
    title: item.title,
    status: item.status || 'not_started',
    progress: Number.isFinite(item.progress) ? item.progress : 0,
    badge: item.badge || '',
    actionItems: Array.isArray(item.actionItems) ? item.actionItems : [],
  }));
}

function barTone(progress, status) {
  if (status === 'completed' || progress >= 100) return 'done';
  if (progress > 0) return 'active';
  return 'idle';
}

function summarizeActions(session) {
  const items = (session.actions || []).flatMap((step) => (
    Array.isArray(step.actionItems) ? step.actionItems : []
  ));
  const done = items.filter((item) => item.status === 'completed').length;
  return { done, total: items.length };
}

function ProgressMeter({ progress, tone, className = '' }) {
  const value = Math.min(100, Math.max(0, Math.round(Number(progress) || 0)));
  return (
    <span className={`${styles.meter} ${className}`.trim()}>
      <span className={styles.meterTrack} aria-hidden="true">
        <span
          className={`${styles.meterFill} ${styles[`meter-${tone}`] || ''}`}
          style={{ width: `${value}%` }}
        />
      </span>
      <span className={styles.meterValue}>
        {value}%
        <span className={styles.srOnly}> complete</span>
      </span>
    </span>
  );
}

function StepMark({ status }) {
  if (status === 'completed') {
    return (
      <span className={`${styles.stepMark} ${styles.stepMarkDone}`} aria-hidden="true">
        <CheckIcon />
      </span>
    );
  }
  if (status === 'in_progress') {
    return <span className={`${styles.stepMark} ${styles.stepMarkActive}`} aria-hidden="true" />;
  }
  return <span className={styles.stepMark} aria-hidden="true" />;
}

function contentCount(sessions) {
  return sessions.reduce((sum, session) => sum + (session.actions?.length || 0), 0);
}

const BOARD_CAROUSEL_QUERY = '(max-width: 768px)';

function BoardCarouselArrow({ direction }) {
  const points = direction === 'left' ? 'M19 12H5M11 6l-6 6 6 6' : 'M5 12h14M13 6l6 6-6 6';
  return (
    <svg className={styles.boardSwapIcon} viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <path
        d={points}
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function useBoardCarousel(slideCount, resetToken) {
  const scrollerRef = useRef(null);
  const indexRef = useRef(0);
  const programmaticRef = useRef(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const [isCarousel, setIsCarousel] = useState(
    () => typeof window !== 'undefined' && window.matchMedia(BOARD_CAROUSEL_QUERY).matches,
  );

  const syncIndex = useCallback((next) => {
    const maxIndex = Math.max(slideCount - 1, 0);
    const clamped = Math.max(0, Math.min(maxIndex, next));
    indexRef.current = clamped;
    setActiveIndex((current) => (current === clamped ? current : clamped));
  }, [slideCount]);

  const readClosest = useCallback(() => {
    const el = scrollerRef.current;
    if (!el) return 0;
    const origin = el.getBoundingClientRect().left;
    let closest = 0;
    let min = Infinity;
    [...el.children].forEach((slide, index) => {
      const delta = Math.abs(slide.getBoundingClientRect().left - origin);
      if (delta < min) {
        min = delta;
        closest = index;
      }
    });
    return closest;
  }, []);

  useEffect(() => {
    indexRef.current = 0;
    setActiveIndex(0);
    scrollerRef.current?.scrollTo({ left: 0, behavior: 'auto' });
  }, [slideCount, resetToken]);

  useEffect(() => {
    const media = window.matchMedia(BOARD_CAROUSEL_QUERY);
    const el = scrollerRef.current;
    const applyMode = () => {
      setIsCarousel(media.matches);
      if (media.matches) syncIndex(readClosest());
    };
    const syncFromScroll = () => {
      if (!media.matches) return;
      if (programmaticRef.current) {
        syncIndex(indexRef.current);
        return;
      }
      syncIndex(readClosest());
    };
    const finishScroll = () => {
      programmaticRef.current = false;
      if (media.matches) syncIndex(readClosest());
    };
    applyMode();
    media.addEventListener('change', applyMode);
    el?.addEventListener('scroll', syncFromScroll, { passive: true });
    el?.addEventListener('scrollend', finishScroll);
    window.addEventListener('resize', syncFromScroll);
    return () => {
      media.removeEventListener('change', applyMode);
      el?.removeEventListener('scroll', syncFromScroll);
      el?.removeEventListener('scrollend', finishScroll);
      window.removeEventListener('resize', syncFromScroll);
    };
  }, [readClosest, syncIndex]);

  const goTo = useCallback((next) => {
    const el = scrollerRef.current;
    if (!el || !window.matchMedia(BOARD_CAROUSEL_QUERY).matches) return;
    const maxIndex = Math.max(slideCount - 1, 0);
    const clamped = Math.max(0, Math.min(maxIndex, next));
    const slide = el.children[clamped];
    if (!slide) return;
    programmaticRef.current = true;
    syncIndex(clamped);
    const left = slide.getBoundingClientRect().left - el.getBoundingClientRect().left + el.scrollLeft;
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    el.scrollTo({ left, behavior: reduce ? 'auto' : 'smooth' });
    window.setTimeout(() => {
      if (!programmaticRef.current) return;
      programmaticRef.current = false;
      syncIndex(indexRef.current);
    }, 520);
  }, [slideCount, syncIndex]);

  return { scrollerRef, activeIndex, isCarousel, goTo, indexRef };
}

function ProgressBoard({
  title,
  titleId,
  count,
  tone,
  sessions,
  openIds,
  menuId,
  onToggle,
  onOpen,
  onMenu,
  onContinueAll,
  menuRef,
  emptyLabel,
  enterDelay = '0.18s',
}) {
  const Icon = tone === 'done' ? CompletedIcon : InProgressIcon;
  const menuOpen = sessions.some((session) => session.id === menuId);
  const slideKey = sessions.map((session) => session.id).join('\0');
  const { scrollerRef, activeIndex, isCarousel, goTo, indexRef } = useBoardCarousel(sessions.length, slideKey);
  const showCarouselControls = isCarousel && sessions.length > 1;

  return (
    <section
      className={`${styles.board} ${styles[`board-${tone}`] || ''} ${menuOpen ? styles.boardRaised : ''}`}
      style={{ animationDelay: enterDelay }}
      aria-labelledby={titleId}
    >
      <div className={styles.boardHeader}>
        <h2 id={titleId} className={styles.boardTitle}>
          <span className={`${styles.boardIcon} ${styles[`boardIcon-${tone}`]}`} aria-hidden="true">
            <Icon />
          </span>
          {title}
          <span className={`${styles.countBadge} ${styles[`countBadge-${tone}`]}`}>{count}</span>
        </h2>
        {onContinueAll ? (
          <button type="button" className={styles.continueAll} onClick={onContinueAll}>
            <PlayIcon />
            Continue All
          </button>
        ) : null}
      </div>

      {sessions.length === 0 ? (
        <p className={styles.boardEmpty}>{emptyLabel}</p>
      ) : (
      <>
      <div className={styles.boardColumns} aria-hidden="true">
        <span>Quick Richuel</span>
        <span>Action Items</span>
        <span>Progress</span>
        <span />
      </div>

      <div
        className={`${styles.boardList} ${menuOpen ? styles.boardListMenuOpen : ''}`}
        ref={scrollerRef}
        tabIndex={showCarouselControls ? 0 : undefined}
        role={showCarouselControls ? 'region' : undefined}
        aria-roledescription={showCarouselControls ? 'carousel' : undefined}
        aria-label={showCarouselControls ? title : undefined}
        onKeyDown={showCarouselControls ? (event) => {
          if (event.key === 'ArrowRight') {
            event.preventDefault();
            goTo(indexRef.current + 1);
          } else if (event.key === 'ArrowLeft') {
            event.preventDefault();
            goTo(indexRef.current - 1);
          }
        } : undefined}
      >
        {sessions.map((session, index) => {
          const steps = actionSteps(session);
          const actions = summarizeActions(session);
          const expanded = openIds.has(session.id);
          const categoryDone = session.status === 'completed';
          const stepsPanelId = `yp-steps-${String(session.id).replace(/[^A-Za-z0-9_-]/g, '-')}`;

          return (
            <article
              key={session.id}
              className={`${styles.sessionBlock} ${expanded ? styles.sessionBlockOpen : ''}`}
              role={showCarouselControls ? 'group' : undefined}
              aria-roledescription={showCarouselControls ? 'slide' : undefined}
              aria-label={showCarouselControls ? `${index + 1} of ${sessions.length}` : undefined}
            >
              <div className={styles.sessionRow}>
                <div className={styles.sessionMain}>
                  <button
                    type="button"
                    className={styles.thumbButton}
                    onClick={() => onOpen(session.openContentId)}
                    aria-label={`Open ${session.title}`}
                  >
                    <span className={styles.thumb}>
                      <img src={session.coverSrc} alt="" />
                      <span
                        className={`${styles.thumbBadge} ${categoryDone ? styles.thumbBadgeDone : styles.thumbBadgePlay}`}
                        aria-hidden="true"
                      >
                        {categoryDone ? <CheckIcon /> : <PlayIcon />}
                      </span>
                    </span>
                  </button>
                  <div className={styles.sessionCopy}>
                    <button
                      type="button"
                      className={styles.sessionTitle}
                      onClick={() => onOpen(session.openContentId)}
                    >
                      {session.title}
                    </button>
                    <button
                      type="button"
                      className={styles.actionToggle}
                      aria-expanded={expanded}
                      aria-controls={stepsPanelId}
                      aria-label={`${expanded ? 'Hide' : 'Show'} ${steps.length} ${steps.length === 1 ? 'Quick Richuel' : 'Quick Richuels'} in ${session.title}`}
                      onClick={() => onToggle(session.id)}
                    >
                      {steps.length} {steps.length === 1 ? 'Quick Richuel' : 'Quick Richuels'}
                      <ChevronDownIcon className={expanded ? styles.chevronOpen : ''} />
                    </button>
                  </div>
                </div>

                <p className={styles.actionSummary}>
                  <span className={styles.actionSummaryLabel}>Action items</span>
                  {actions.total > 0 ? (
                    <span className={styles.actionSummaryValue}>
                      <span className={styles.srOnly}>{actions.done} of {actions.total} done</span>
                      <span
                        className={`${styles.actionSummaryCount} ${actions.done === actions.total ? styles.actionSummaryCountDone : ''}`}
                        aria-hidden="true"
                      >
                        {actions.done}
                      </span>
                      <span className={styles.actionSummarySlash} aria-hidden="true">/</span>
                      <span className={styles.actionSummaryTotal} aria-hidden="true">{actions.total}</span>
                      <span className={styles.actionSummaryOf} aria-hidden="true">done</span>
                    </span>
                  ) : (
                    <span className={styles.actionSummaryEmpty}>None yet</span>
                  )}
                </p>
                <ProgressMeter
                  progress={session.progress}
                  tone={barTone(session.progress, session.status)}
                  className={styles.sessionMeter}
                />

                <div className={styles.menuWrap} ref={menuId === session.id ? menuRef : null}>
                  <button
                    type="button"
                    className={styles.menuButton}
                    aria-label={`More options for ${session.title}`}
                    aria-expanded={menuId === session.id}
                    onClick={() => onMenu(session.id)}
                  >
                    <MoreIcon />
                  </button>
                  {menuId === session.id ? (
                    <div className={styles.menu} role="menu">
                      <button
                        type="button"
                        role="menuitem"
                        className={styles.menuItem}
                        onClick={() => onOpen(session.openContentId)}
                      >
                        Open session
                      </button>
                    </div>
                  ) : null}
                </div>
              </div>

              <div
                id={stepsPanelId}
                className={`${styles.stepReveal} ${expanded ? styles.stepRevealOpen : ''}`}
                inert={expanded ? undefined : true}
              >
                <ul className={styles.stepList}>
                  {steps.map((step) => (
                    <li key={step.id} className={styles.stepRow}>
                      <div className={styles.stepMain}>
                        <button
                          type="button"
                          className={styles.stepTitle}
                          onClick={() => onOpen(step.id)}
                        >
                          <span className={styles.stepTitleText}>{step.title}</span>
                          <ChevronRightIcon className={styles.stepTitleArrow} />
                        </button>
                        {step.status === 'completed' ? (
                          <span className={`${styles.inlinePill} ${styles.inlinePillDone}`}>Completed</span>
                        ) : null}
                        {step.status === 'in_progress' ? (
                          <span className={`${styles.inlinePill} ${styles.inlinePillActive}`}>
                            {step.badge || 'In progress'}
                          </span>
                        ) : null}
                        {step.status !== 'completed' && step.status !== 'in_progress' ? (
                          <span className={`${styles.inlinePill} ${styles.inlinePillIdle}`}>Not started</span>
                        ) : null}
                      </div>
                      {step.actionItems.length === 0 ? (
                        <p className={styles.actionNone}>No action items</p>
                      ) : (
                        <ul className={styles.actionGlance} aria-label={`Action items for ${step.title}`}>
                          {step.actionItems.map((item) => (
                            <li key={item.id} className={styles.actionGlanceItem}>
                              <StepMark status={item.status} />
                              <span className={styles.actionGlanceTitle}>{item.title}</span>
                              <span className={styles.srOnly}>
                                {item.status === 'completed' ? 'Completed' : 'Not completed'}
                              </span>
                            </li>
                          ))}
                        </ul>
                      )}
                      <ProgressMeter progress={step.progress} tone={barTone(step.progress, step.status)} />
                      <span className={styles.stepMenuSpacer} />
                    </li>
                  ))}
                </ul>
              </div>
            </article>
          );
        })}
      </div>
      {showCarouselControls && (
        <div className={styles.boardNav}>
          <button
            type="button"
            className={styles.boardSwap}
            aria-label={`Previous ${title}`}
            disabled={activeIndex === 0}
            onClick={() => goTo(activeIndex - 1)}
          >
            <BoardCarouselArrow direction="left" />
          </button>
          <div className={styles.boardDots}>
            {sessions.map((session, index) => (
              <button
                key={session.id}
                type="button"
                className={index === activeIndex ? `${styles.boardDot} ${styles.boardDotActive}` : styles.boardDot}
                aria-label={session.title}
                aria-current={index === activeIndex ? 'true' : undefined}
                onClick={() => goTo(index)}
              />
            ))}
          </div>
          <button
            type="button"
            className={styles.boardSwap}
            aria-label={`Next ${title}`}
            disabled={activeIndex >= sessions.length - 1}
            onClick={() => goTo(activeIndex + 1)}
          >
            <BoardCarouselArrow direction="right" />
          </button>
          {sessions[activeIndex] && (
            <p className={styles.boardPosition} aria-hidden="true">
              {activeIndex + 1} of {sessions.length}
            </p>
          )}
          {sessions[activeIndex] && (
            <p className={styles.boardLive} aria-live="polite">
              {sessions[activeIndex].title}, {activeIndex + 1} of {sessions.length}
            </p>
          )}
        </div>
      )}
      </>
      )}
    </section>
  );
}

function ProgressSkeleton() {
  return (
    <div className={styles.skeleton} aria-hidden="true">
      <div className={styles.statsGrid}>
        {[0, 1, 2].map((key) => (
          <div key={key} className={styles.skeletonCard}>
            <span className={`${styles.bone} ${styles.boneIcon}`} />
            <span className={styles.skeletonCopy}>
              <span className={`${styles.bone} ${styles.boneLineShort}`} />
              <span className={`${styles.bone} ${styles.boneLine}`} />
              <span className={`${styles.bone} ${styles.boneLineMid}`} />
            </span>
          </div>
        ))}
      </div>
      <div className={styles.skeletonBoard}>
        <span className={`${styles.bone} ${styles.boneTitle}`} />
        {[0, 1, 2].map((key) => (
          <div key={key} className={styles.skeletonRow}>
            <span className={`${styles.bone} ${styles.boneThumb}`} />
            <span className={styles.skeletonCopy}>
              <span className={`${styles.bone} ${styles.boneLine}`} />
              <span className={`${styles.bone} ${styles.boneLineMid}`} />
            </span>
            <span className={`${styles.bone} ${styles.bonePill}`} />
          </div>
        ))}
      </div>
    </div>
  );
}

function YourProgressPage() {
  const navigate = useNavigate();
  const firstName = getFirstNameFromStorage() || 'there';
  const [categories, setCategories] = useState([]);
  const [contents, setContents] = useState([]);
  const [recommendedCategory, setRecommendedCategory] = useState('');
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const { inProgress: inProgressSessions, completed: completedSessions } = useMemo(
    () => buildProgressBoards(categories, contents, readPlaybackStartedIds()),
    [categories, contents],
  );
  const sessions = useMemo(
    () => [...inProgressSessions, ...completedSessions],
    [inProgressSessions, completedSessions],
  );
  const summary = useMemo(() => summarizeContents(contents), [contents]);
  const upcoming = useMemo(
    () => nextContent(contents, recommendedCategory),
    [contents, recommendedCategory],
  );
  const [collapsedIds, setCollapsedIds] = useState(() => new Set());
  const defaultsApplied = useRef(false);
  const openIds = useMemo(() => {
    const open = new Set(sessions.map((session) => session.id));
    collapsedIds.forEach((id) => open.delete(id));
    return open;
  }, [sessions, collapsedIds]);
  const [menuId, setMenuId] = useState(null);
  const menuRef = useRef(null);
  const statsReady = !loading && !loadError;
  const weekCount = useCountUp(summary.completedThisWeek, statsReady);
  const actionCount = useCountUp(summary.actionsCompleted, statsReady);
  const listenedCount = useCountUp(summary.watched, statsReady);

  useEffect(() => {
    if (defaultsApplied.current || sessions.length === 0) return;
    defaultsApplied.current = true;
    const completedIds = sessions
      .filter((session) => session.status === 'completed')
      .map((session) => session.id);
    if (completedIds.length) setCollapsedIds(new Set(completedIds));
  }, [sessions]);

  useEffect(() => {
    const ac = new AbortController();
    const { signal } = ac;
    const run = async () => {
      setLoading(true);
      setLoadError(false);
      try {
        const [categoryResult, contentResult] = await Promise.all([
          fetchUserCategories(navigate, signal),
          fetchUserContents(navigate, { excludeNotStarted: true, signal }),
        ]);
        if (signal.aborted) return;
        if (!categoryResult || !contentResult) {
          setLoadError(true);
          setCategories([]);
          setContents([]);
          return;
        }
        setCategories(Array.isArray(categoryResult.categories) ? categoryResult.categories : []);
        setRecommendedCategory(categoryResult.recommended_category || '');
        setContents(Array.isArray(contentResult.contents) ? contentResult.contents : []);
      } catch (error) {
        if (error?.name === 'AbortError' || signal.aborted) return;
        handleApiError(error, navigate, false);
        setLoadError(true);
      } finally {
        if (!signal.aborted) setLoading(false);
      }
    };
    run();
    return () => ac.abort();
  }, [navigate]);

  useEffect(() => {
    if (!menuId) return undefined;
    const close = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) setMenuId(null);
    };
    const onKey = (event) => {
      if (event.key === 'Escape') setMenuId(null);
    };
    document.addEventListener('mousedown', close);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', close);
      document.removeEventListener('keydown', onKey);
    };
  }, [menuId]);

  const openSession = (contentId) => {
    setMenuId(null);
    if (contentId) navigate(sessionPath(contentId));
  };

  const toggleSession = (sessionId) => {
    setCollapsedIds((prev) => {
      const next = new Set(prev);
      if (next.has(sessionId)) next.delete(sessionId);
      else next.add(sessionId);
      return next;
    });
  };

  return (
    <div className={styles.page} data-page="your-progress" aria-busy={loading}>
      <div className={styles.mainContent}>
        <header className={styles.heroBanner}>
          <div className={styles.heroCopy}>
            <h1 className={styles.heroTitle}>Small steps. Real progress.</h1>
            <p className={styles.heroSubtitle}>
              You&apos;re building a calmer, brighter financial future, one intentional action at a time.
            </p>
            <div className={styles.heroActions}>
              <button
                type="button"
                className={styles.heroCta}
                onClick={() => openSession(upcoming?.id)}
                disabled={!upcoming}
              >
                <span className={styles.heroCtaPlay} aria-hidden="true">
                  <PlayIcon />
                </span>
                Continue Next Quick Richuel
              </button>
              {upcoming ? (
                <p className={styles.heroNext}>
                  Next: {upcoming.title}
                </p>
              ) : null}
            </div>
          </div>
          <picture>
            <source media="(min-width: 769px)" srcSet={heroMountainsWide} />
            <img
              src={heroMountains}
              alt=""
              className={styles.heroArt}
              decoding="async"
            />
          </picture>
        </header>

        {loading ? <ProgressSkeleton /> : null}

        {!loading && loadError ? (
          <p className={styles.notice} role="alert">
            We couldn&apos;t load your progress. Refresh and try again.
          </p>
        ) : null}

        {!loading && !loadError ? (
          <>
            <section className={styles.statsGrid} aria-label="Progress snapshot">
              <article className={styles.statCard}>
                <PaceRing
                  completed={summary.completedThisWeek}
                  total={summary.total}
                  shownCompleted={weekCount}
                />
                <div className={styles.statCopy}>
                  <p className={styles.statEyebrow}>Weekly Pace</p>
                  <h2 className={styles.statValue}>This Week</h2>
                  <p className={styles.statHint}>
                    {weekCount} of {summary.total} Quick Richuels completed
                  </p>
                </div>
              </article>

              <article className={styles.statCard}>
                <span className={`${styles.statIcon} ${styles.statIconTeal}`} aria-hidden="true">
                  <TrendUpIcon />
                </span>
                <div className={styles.statCopy}>
                  <p className={styles.statEyebrow}>Actions Done</p>
                  <h2 className={styles.statValue}>{actionCount}</h2>
                  <p className={styles.statHint}>Action items completed</p>
                </div>
              </article>

              <article className={styles.statCard}>
                <span className={`${styles.statIcon} ${styles.statIconGold}`} aria-hidden="true">
                  <CalendarIcon />
                </span>
                <div className={styles.statCopy}>
                  <p className={styles.statEyebrow}>Listened</p>
                  <h2 className={styles.statValue}>{listenedCount}</h2>
                  <p className={styles.statHint}>Finished listening</p>
                </div>
              </article>
            </section>

            <ProgressBoard
              title="In Progress"
              titleId="in-progress-title"
              count={contentCount(inProgressSessions)}
              tone="active"
              sessions={inProgressSessions}
              openIds={openIds}
              menuId={menuId}
              menuRef={menuRef}
              onToggle={toggleSession}
              onOpen={openSession}
              onMenu={(sessionId) => setMenuId((current) => (current === sessionId ? null : sessionId))}
              onContinueAll={
                inProgressSessions.length
                  ? () => openSession(inProgressSessions[0].openContentId)
                  : undefined
              }
              emptyLabel="Nothing left in progress. Finished Quick Richuels are in Completed."
              enterDelay="0.16s"
            />

            <ProgressBoard
              title="Completed"
              titleId="completed-title"
              count={contentCount(completedSessions)}
              tone="done"
              sessions={completedSessions}
              openIds={openIds}
              menuId={menuId}
              menuRef={menuRef}
              onToggle={toggleSession}
              onOpen={openSession}
              onMenu={(sessionId) => setMenuId((current) => (current === sessionId ? null : sessionId))}
              emptyLabel="Finished Quick Richuels will land here."
              enterDelay="0.26s"
            />
          </>
        ) : null}

        <div className={styles.footerReveal}>
          <ForecastProgressFooter
            title="Keep going!"
            subtitle={`Every small action compounds into a brighter tomorrow. You're doing great, ${firstName}!`}
            onStartMove={() => navigate(SEE_ALL_MONEY_MOVES_PATH)}
            ariaLabel="Keep going"
          />
        </div>
      </div>
    </div>
  );
}

export default YourProgressPage;
